import ValueMapper from "./value-mapper";

const ADMIN = "ADMIN";
const READER_ACCOUNT = "READER.ACCOUNT";
const READER_HACCP = "READER.HACCP";
const READER_ALL = "READER.ALL";
const COMMON = "COMMON";

// ロール：管理者
const ROLE_INDEX_ADMIN = 1;
// ロール：閲覧者
const ROLE_INDEX_READER = 2;
// ロール：権限なし
const ROLE_INDEX_NONE = 3;

// 許可範囲：未定義
const AREA_INDEX_ANY = 0;
// 許可範囲：アカウントのみ
const AREA_INDEX_ACCOUNT = 1;
// 許可範囲：HACCPのみ
const AREA_INDEX_HACCP = 2;
// 許可範囲：全て
const AREA_INDEX_ALL = 3;

// グループ種別：通常グループ
const GROUP_TYPE_GROUP = 1;

/**
 * セレクトボックス向けのロール情報を作成する
 */
class RolesBuilder {
  /**
   * 目的に応じてロールを返却する
   * @param {目的} require
   * @returns セレクトボックスで利用するロール情報
   */
  createRoles(require) {
    let roles = [];
    if (require.includes(ADMIN)) {
      roles.push({
        text: "グループ管理者",
        value: { role: ROLE_INDEX_ADMIN, area: AREA_INDEX_ANY },
      });
    }
    if (require.includes(READER_ALL)) {
      roles.push({
        text: "閲覧者(全て)",
        value: { role: ROLE_INDEX_READER, area: AREA_INDEX_ALL },
      });
    }
    if (require.includes(READER_ACCOUNT)) {
      roles.push({
        text: "閲覧者(アカウント)",
        value: { role: ROLE_INDEX_READER, area: AREA_INDEX_ACCOUNT },
      });
    }
    if (require.includes(READER_HACCP)) {
      roles.push({
        text: "閲覧者(HACCP)",
        value: { role: ROLE_INDEX_READER, area: AREA_INDEX_HACCP },
      });
    }
    if (require.includes(COMMON)) {
      roles.push({
        text: "権限なし",
        value: { role: ROLE_INDEX_NONE, area: AREA_INDEX_ANY },
      });
    }
    return roles;
  }

  /**
   * 管理者であればtrueを返す
   */
  isAdmin(role) {
    if (role == ROLE_INDEX_ADMIN) {
      return true;
    }
    return false;
  }

  /**
   * 閲覧者であればtrueを返す
   */
  isReader(role) {
    if (role == ROLE_INDEX_READER) {
      return true;
    }
    return false;
  }

  /**
   * ロールを対応する色に変換する
   */
  roleToRoleColor(role) {
    return ValueMapper.map({
      [ROLE_INDEX_ADMIN]: "primary",
      [ROLE_INDEX_READER]: "orange",
    }).getOrElse(role, "green");
  }

  /**
   * ロールを詳細説明に変換する
   */
  roleToRoleDescription(targetName, role, area) {
    // ベースの文言を選択する
    const roleString = ValueMapper.map({
      [ROLE_INDEX_ADMIN]:
        "「%1」に所属するユーザーを<br />管理、編集することができます",
      [ROLE_INDEX_READER]:
        "「%1」に所属するユーザーの<br />%2を閲覧することができます",
    }).getOrElse(
      role,
      "「%1」に所属する自分以外のユーザーに対して、<br />このユーザーは権限がありません"
    );

    // 権限範囲を文字列に変換する
    const areaString = ValueMapper.map({
      [AREA_INDEX_ACCOUNT]: "アカウント情報",
      [AREA_INDEX_HACCP]: "HACCP情報",
      [AREA_INDEX_ALL]: "全ての情報",
    }).getOrElse(area, "-");

    // 長い文字列を省略する
    const ellipsisString = (content, contentMaxLength) => {
      if (content.length <= contentMaxLength) {
        return content;
      }
      return content.substring(0, contentMaxLength) + "…";
    };

    return roleString
      .replace("%1", ellipsisString(targetName, 10))
      .replace("%2", areaString);
  }

  /**
   * ロール、権限範囲から、セレクトボックスで表示するロール情報を取得する
   * @param {ロール} role
   * @param {権限範囲} area
   */
  roleFromValue(role, area) {
    const defines = this.createRoles([
      ADMIN,
      READER_ACCOUNT,
      READER_ALL,
      COMMON,
    ]);
    const roles = defines.filter((def) => {
      if (def.value.role == role) {
        // 閲覧者でなければ、権限範囲を問わない
        if (role != ROLE_INDEX_READER) {
          return true;
        }
        // 閲覧者であれば、権限範囲を確認する
        if (def.value.area == area) {
          return true;
        }
      }
      return false;
    });
    if (roles.length >= 1) {
      return roles[0];
    }
    return defines[0];
  }

  /**
   * 所属グループの候補を取得する
   */
  joinRoles() {
    return this.createRoles([ADMIN, READER_ALL, READER_ACCOUNT, COMMON]);
  }

  /**
   * グループ権限の候補を取得する
   */
  permissionRoles() {
    return this.createRoles([ADMIN, READER_ALL, READER_ACCOUNT, COMMON]);
  }

  /**
   * メールアドレスによる権限付与の候補を取得する
   */
  linkRoles() {
    return this.createRoles([ADMIN, READER_ALL, READER_ACCOUNT]);
  }

  /**
   * グループ対グループの権限の候補を取得する
   */
  groupToGroupRoles() {
    return this.createRoles([READER_ALL, READER_ACCOUNT]);
  }

  /**
   * 配列の中にデータがあるのならtrueを返す
   */
  containsInList(dataList, itemId) {
    return dataList.some((item) => item.value == itemId);
  }

  /**
   * 権限情報をセレクトボックス向けのデータに変換する
   * @param {ユーザー権限の配列} userPermissions
   * @param {管理可能なグループの一覧} adminPermissionResources
   * @param {ユーザー権限の表示条件（フィルタ関数）} filterFunction
   * @param {true:管理可能なグループだけを取得、false:管理不可のグループだけを取得} requireToAdmin
   * @param {グループ編集可否の定義関数} group_editable
   * @param {permissionから取得するキー。destination, owner} targetKey
   * @returns
   */
  convertPermissionData(
    userPermissions,
    adminPermissionResources,
    filterFunction,
    requireToAdmin,
    group_editable,
    targetKey = "destination"
  ) {
    return (
      userPermissions
        .filter(filterFunction)
        // 管理者権限が必要なもの、必要ないものをより分ける
        .filter((permission) => {
          return (
            this.containsInList(
              adminPermissionResources,
              permission[targetKey].group_id
            ) == requireToAdmin
          );
        })
        .map((permission) => {
          let is_group = false;
          if (permission.destination && permission.owner) {
            // 権限の所有者、付与先から、グループを判定する
            is_group =
              permission.destination.type == GROUP_TYPE_GROUP &&
              permission.owner.type == GROUP_TYPE_GROUP
                ? true
                : false;
          }
          return {
            // メタ情報
            meta: {
              // 権限ID
              permission_id: permission.permission_id,
              // 編集状態
              edit: false,
              // 種別
              type: {
                group_editable: group_editable(is_group),
              },
            },
            // 対象グループ名
            name: {
              text: permission[targetKey].display_name,
              value: permission[targetKey].group_id,
            },
            // 対象ロール情報
            role: this.roleFromValue(permission.role, permission.area),
          };
        })
        .map((data) => {
          if (requireToAdmin) {
            // 編集権限のあるものはそのまま返却する
            return data;
          } else {
            // 編集権限のないものはセレクトボックスではなく文字列で表示する
            // 表示文字列をdisplayとして取得する
            return {
              display: `${data.name.text} 【${data.role.text}】`,
              data: data,
            };
          }
        })
    );
  }
}

export default new RolesBuilder();
